import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { companyApi } from '@/api';
import { Alert, Button, ErrorState, LoadingState, Modal, useToast } from '@/components/ui';
import { errorMessage } from '@/hooks/useApiError';

import { PermissionChecklist } from './PermissionChecklist';
import { ACCESS_KEYS, roleLabel, sameSet } from './roles';

/**
 * One member's permissions, edited independently of everyone else in their
 * role. What is saved is the whole set; the API stores it as differences from
 * the role, so a later change to the role still reaches this member for every
 * permission that was not set individually.
 */
export function MemberPermissionsModal({ member, onClose }) {
  const open = Boolean(member);
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ACCESS_KEYS.member(member?.id),
    queryFn: () => companyApi.getMemberPermissions(member.id),
    enabled: open,
  });

  return (
    <Modal
      open={open}
      size="xl"
      title={member ? `Permissions for ${member.full_name}` : 'Permissions'}
      description={
        data
          ? `${roleLabel(data.role)} role. Changes apply to this person only, from their next request.`
          : undefined
      }
      onClose={onClose}
    >
      {isPending && <LoadingState label="Loading permissions…" />}
      {isError && <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />}
      {/* Keyed so reopening for another member starts from their saved set. */}
      {data && <Editor key={data.member_id} access={data} onClose={onClose} />}
    </Modal>
  );
}

function Editor({ access, onClose }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const roleSet = new Set(access.role_permissions);
  const saved = new Set(access.permissions);
  const [selected, setSelected] = useState(saved);

  const save = useMutation({
    mutationFn: (permissions) => companyApi.updateMemberPermissions(access.member_id, permissions),
    onSuccess: (updated) => {
      queryClient.setQueryData(ACCESS_KEYS.member(access.member_id), updated);
      void queryClient.invalidateQueries({ queryKey: ['company-members'] });
      toast.success('Permissions updated');
      onClose();
    },
    onError: (saveError) => toast.error(errorMessage(saveError, 'Could not save the permissions')),
  });

  if (access.locked) {
    return (
      <Alert tone="info" title="Always full access">
        {access.is_owner ? 'The workspace owner' : 'Admins'} always hold every permission, so the
        team can never be left without someone able to manage it. Change the role on the Team page
        to limit what this person can do.
      </Alert>
    );
  }

  function toggle(code) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  const unchanged = sameSet(selected, saved);
  const matchesRole = sameSet(selected, roleSet);

  return (
    <div className="space-y-5">
      <PermissionChecklist
        catalogue={access.catalogue}
        selected={selected}
        baseline={roleSet}
        onToggle={toggle}
        disabled={save.isPending}
      />

      <div className="border-line flex flex-col-reverse gap-2 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
        <Button
          variant="ghost"
          type="button"
          disabled={matchesRole || save.isPending}
          onClick={() => setSelected(new Set(roleSet))}
        >
          Match the role
        </Button>
        <div className="flex flex-col-reverse gap-2 sm:flex-row">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={unchanged}
            isLoading={save.isPending}
            onClick={() => save.mutate([...selected])}
          >
            Save permissions
          </Button>
        </div>
      </div>
    </div>
  );
}
